import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PaymentGatewayAdapter, PaymentToken, GatewayConfig, PaymentData } from '../../types/payment';
import { StripeGatewayAdapter } from '../../services/payment/StripeGatewayAdapter';
import { SquareGatewayAdapter } from '../../services/payment/SquareGatewayAdapter';
import { authService } from '../../services/auth/authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8004/api';

interface AdminPaymentCardEntryProps {
  storeId: number;
  amount: number;
  currency?: string;
  onTokenize: (token: PaymentToken) => void;
  onError: (error: string) => void;
  disabled?: boolean;
  adminLevel: number;
  requiredLevel?: number;
  /**
   * What the manager does with the token once the card validates. Wording only —
   * it has to name the button the caller actually renders next.
   */
  nextActionLabel?: string;
  /**
   * Bump to discard the "Card accepted" result. A token is spent by the charge
   * attempt it was minted for, so once the caller has used one — successfully or
   * not — leaving the acceptance message on screen invites the manager to charge
   * a token that no longer works. Changing this value clears it and puts them
   * back on "Validate Card".
   */
  resetSignal?: number;
}

interface VerbalAuthorizationState {
  confirmed: boolean;
  timestamp: string | null;
}

/**
 * Admin Payment Card Entry Component
 *
 * Security features:
 * - Admin level restriction (managers level 10+ only)
 * - Verbal authorization confirmation required
 * - Uses Stripe Elements or Square Web Payments (PCI compliant)
 * - Card data never touches our servers
 */
const AdminPaymentCardEntry: React.FC<AdminPaymentCardEntryProps> = ({
  storeId,
  amount,
  currency = 'USD',
  onTokenize,
  onError,
  disabled = false,
  adminLevel,
  requiredLevel = 10,
  nextActionLabel = 'Click "Next" to process the payment.',
  resetSignal = 0
}) => {
  const [gateway, setGateway] = useState<PaymentGatewayAdapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [cardholderName, setCardholderName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [tokenizeResult, setTokenizeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [verbalAuth, setVerbalAuth] = useState<VerbalAuthorizationState>({
    confirmed: false,
    timestamp: null
  });

  const gatewayInitRef = useRef(false);
  const cardElementRef = useRef(false);
  const gatewayRef = useRef<PaymentGatewayAdapter | null>(null);

  // Use refs for callback props to prevent re-running effects when parent re-renders
  const onTokenizeRef = useRef(onTokenize);
  const onErrorRef = useRef(onError);
  useEffect(() => { onTokenizeRef.current = onTokenize; }, [onTokenize]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useEffect(() => {
    gatewayRef.current = gateway;
  }, [gateway]);

  // Drop the stale "Card accepted" message when the caller signals the token was
  // used up. The gateway and the card element are deliberately left alone: the
  // manager usually only needs to correct a digit or two, and re-initializing
  // would wipe what they have already typed.
  useEffect(() => {
    if (resetSignal > 0) {
      setTokenizeResult(null);
    }
  }, [resetSignal]);

  const hasPermission = adminLevel >= requiredLevel;

  const initializeGateway = useCallback(async () => {
    if (gatewayInitRef.current) return;
    gatewayInitRef.current = true;

    try {
      setLoading(true);

      const token = authService.getAuthToken();
      const response = await fetch(`${API_BASE_URL}/store-payment-providers?store_id=${storeId}&active_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payment provider configuration');
      }

      const data = await response.json();

      if (!data.configurations || data.configurations.length === 0) {
        throw new Error('No payment providers configured for this store');
      }

      // Get the primary provider (highest priority)
      const primaryProvider = data.configurations[0];
      const providerCode = primaryProvider.provider?.provider_code;
      const credentials = primaryProvider.credentials;

      let gatewayAdapter: PaymentGatewayAdapter;
      let config: GatewayConfig & { locationId?: string };

      if (providerCode === 'stripe') {
        gatewayAdapter = new StripeGatewayAdapter();
        config = {
          provider: 'stripe',
          publicKey: credentials.publishable_key || credentials.public_key,
          environment: primaryProvider.environment === 'production' ? 'production' : 'sandbox'
        };
      } else if (providerCode === 'square') {
        gatewayAdapter = new SquareGatewayAdapter();
        config = {
          provider: 'square',
          publicKey: credentials.application_id || credentials.app_id,
          environment: primaryProvider.environment === 'production' ? 'production' : 'sandbox',
          locationId: credentials.location_id
        };
      } else {
        throw new Error(`Unsupported payment provider: ${providerCode}`);
      }

      await gatewayAdapter.initialize(config);
      setGateway(gatewayAdapter);

      console.log(`Admin payment gateway initialized: ${providerCode}`);
    } catch (err) {
      console.error('Failed to initialize payment gateway:', err);
      onErrorRef.current(err instanceof Error ? err.message : 'Failed to initialize payment gateway');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (hasPermission) {
      initializeGateway();
    }

    return () => {
      cardElementRef.current = false;
      gatewayInitRef.current = false;
      if (gatewayRef.current) {
        gatewayRef.current.cleanup();
        gatewayRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  useEffect(() => {
    if (!gateway || cardElementRef.current) return;

    let timeoutId: NodeJS.Timeout;
    let isMounted = true;

    const createCardElement = async () => {
      if (!isMounted || cardElementRef.current) return;

      try {
        if (gateway.provider === 'stripe') {
          const container = document.getElementById('admin-stripe-card-element');
          if (container) container.innerHTML = '';
          (gateway as StripeGatewayAdapter).createCardElement('admin-stripe-card-element');
          cardElementRef.current = true;
        } else if (gateway.provider === 'square') {
          const container = document.getElementById('admin-square-card-element');
          if (container) container.innerHTML = '';
          await (gateway as SquareGatewayAdapter).createCardElement('admin-square-card-element');
          cardElementRef.current = true;
        }
      } catch (err) {
        console.error('Failed to create card element:', err);
        if (isMounted) {
          onErrorRef.current('Failed to create card input');
        }
      }
    };

    timeoutId = setTimeout(createCardElement, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      cardElementRef.current = false;
    };
  }, [gateway]);

  const handleVerbalAuthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerbalAuth({
      confirmed: e.target.checked,
      timestamp: e.target.checked ? new Date().toISOString() : null
    });
  };

  const handleTokenize = async () => {
    if (!gateway || !verbalAuth.confirmed || !cardholderName.trim()) {
      onErrorRef.current('Please complete all required fields');
      return;
    }

    setProcessing(true);
    setTokenizeResult(null);
    try {
      const paymentData: PaymentData = {
        cardNumber: '',
        expiryMonth: '',
        expiryYear: '',
        cvv: '',
        cardholderName: cardholderName.trim(),
        billingAddress: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US'
        },
        amount,
        currency
      };

      const token = await gateway.tokenizePayment(paymentData);

      const enrichedToken = {
        ...token,
        adminAudit: {
          verbalAuthConfirmed: true,
          verbalAuthTimestamp: verbalAuth.timestamp,
          adminEnteredCard: true
        }
      };

      setTokenizeResult({ success: true, message: `Card accepted: ${token.card_brand || 'Card'} ending in ${token.last_four_digits}. ${nextActionLabel}` });
      onTokenizeRef.current(enrichedToken as PaymentToken);
    } catch (err) {
      console.error('Tokenization failed:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to process card';
      setTokenizeResult({ success: false, message: errorMsg });
      onErrorRef.current(errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  const formatAmount = (amt: number): string => {
    return amt.toFixed(2);
  };

  if (!hasPermission) {
    return (
      <div className="admin-payment-card-entry">
        <div className="info-box error">
          <strong>Permission Denied</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Manager level (10+) required to process credit card payments.
          </p>
          <p style={{ margin: '0.25rem 0 0' }}>Your current level: {adminLevel}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-payment-card-entry">
        <div style={{ textAlign: 'center', padding: '1rem' }}>
          <p>Initializing secure payment form...</p>
        </div>
      </div>
    );
  }

  if (!gateway) {
    return (
      <div className="admin-payment-card-entry">
        <div className="info-box error">
          <strong>Payment Unavailable</strong>
          <p style={{ margin: '0.5rem 0 0' }}>
            Unable to initialize payment gateway. Please try again or contact support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-payment-card-entry">
      {/* Security Notice */}
      <div className="info-box warning" style={{ marginBottom: '1rem' }}>
        <strong>PCI Compliance Notice</strong>
        <p style={{ fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
          Card data is processed securely via {gateway.provider === 'stripe' ? 'Stripe' : 'Square'}.
          Customer must verbally authorize this charge.
        </p>
      </div>

      {/* Cardholder Name */}
      <div className="form-group">
        <label>Cardholder Name *</label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name as it appears on card"
          disabled={disabled || processing}
          className="form-input"
        />
      </div>

      {/* Card Element */}
      <div className="form-group">
        <label>Card Details *</label>
        {gateway.provider === 'stripe' && (
          <div
            id="admin-stripe-card-element"
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#fff',
              minHeight: '44px'
            }}
          />
        )}
        {gateway.provider === 'square' && (
          <div
            id="admin-square-card-element"
            style={{
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              padding: '12px',
              backgroundColor: '#fff',
              minHeight: '50px'
            }}
          />
        )}
      </div>

      {/* Verbal Authorization */}
      <div className="form-group" style={{ marginTop: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={verbalAuth.confirmed}
            onChange={handleVerbalAuthChange}
            disabled={disabled || processing}
            style={{ marginTop: '0.25rem' }}
          />
          <span>
            <strong>Customer Verbal Authorization *</strong>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
              I confirm the customer has verbally authorized this ${formatAmount(amount)} {currency} charge
              to their credit card.
            </p>
          </span>
        </label>
      </div>

      {/* Process Button */}
      <button
        type="button"
        onClick={handleTokenize}
        disabled={disabled || processing || !verbalAuth.confirmed || !cardholderName.trim()}
        className="btn btn-primary"
        style={{
          marginTop: '1rem',
          width: '100%',
          padding: '0.75rem',
          fontSize: '1rem',
          opacity: (disabled || processing || !verbalAuth.confirmed || !cardholderName.trim()) ? 0.6 : 1
        }}
      >
        {processing ? 'Processing...' : `Validate Card - $${formatAmount(amount)}`}
      </button>

      {/* Tokenization Result Feedback */}
      {tokenizeResult && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.75rem',
          borderRadius: '6px',
          backgroundColor: tokenizeResult.success ? '#ecfdf5' : '#fef2f2',
          border: `1px solid ${tokenizeResult.success ? '#a7f3d0' : '#fecaca'}`,
          color: tokenizeResult.success ? '#065f46' : '#991b1b',
          fontSize: '0.875rem',
          fontWeight: 500
        }}>
          {tokenizeResult.success ? '\u2713 ' : '\u2717 '}{tokenizeResult.message}
        </div>
      )}

      {/* Audit Trail Note */}
      <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem', textAlign: 'center' }}>
        This transaction will be logged for audit purposes.
      </p>
    </div>
  );
};

export default AdminPaymentCardEntry;
